import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@services/user.context/user.context.service';
import { DeviceAuthRequestInfo, DeviceAuthService } from '../device-auth.service';

type ScreenState = 'loading' | 'confirm' | 'approved' | 'denied' | 'error';

/**
 * The approval screen for a command-line sign-in.
 *
 * The security of the whole exchange rests on one thing this screen must make
 * easy: the operator compares the code shown here against the code their
 * terminal printed. Without that comparison, anyone who can get an operator to
 * open a `/cli-auth` link harvests a full operator token. So the code is the
 * loudest element on the page, nothing is approved automatically, and the
 * request has to be answered deliberately.
 */
@Component({
  standalone: false,
  selector: 'app-cli-auth',
  templateUrl: './cli-auth.component.html',
  styleUrls: ['./cli-auth.component.scss'],
})
export class CliAuthComponent implements OnInit {
  state: ScreenState = 'loading';

  errorMessage = '';

  userCode = '';

  request: DeviceAuthRequestInfo | null = null;

  operatorEmail = '';

  submitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private deviceAuth: DeviceAuthService
  ) {}

  ngOnInit(): void {
    this.userCode = (this.route.snapshot.queryParamMap.get('code') || '').trim();

    if (!this.userCode) {
      this.fail('No code was supplied. Re-run the command that sent you here.');
      return;
    }

    // Sign in first, then come back to this exact request rather than landing
    // on the dashboard and losing the code.
    if (!this.userService.isAuthenticated()) {
      this.router.navigate(['/signin'], {
        queryParams: { returnUrl: `/cli-auth?code=${this.userCode}` },
      });
      return;
    }

    this.operatorEmail = this.userService.getUser()?.email ?? '';
    this.load();
  }

  private load(): void {
    this.deviceAuth.describe(this.userCode).subscribe({
      next: (request) => {
        this.request = request;
        if (request.status === 'approved') {
          this.state = 'approved';
        } else if (request.status === 'denied') {
          this.state = 'denied';
        } else {
          this.state = 'confirm';
        }
      },
      error: (error) => {
        this.fail(
          error?.error?.message ??
            'That code is not valid, or it has already expired.'
        );
      },
    });
  }

  approve(): void {
    if (this.submitting) return;
    this.submitting = true;
    this.deviceAuth.approve(this.userCode).subscribe({
      next: () => {
        this.submitting = false;
        this.state = 'approved';
      },
      error: (error) => {
        this.submitting = false;
        this.fail(
          error?.error?.message ?? 'Could not approve this request. Try again.'
        );
      },
    });
  }

  deny(): void {
    if (this.submitting) return;
    this.submitting = true;
    this.deviceAuth.deny(this.userCode).subscribe({
      next: () => {
        this.submitting = false;
        this.state = 'denied';
      },
      error: (error) => {
        this.submitting = false;
        this.fail(
          error?.error?.message ?? 'Could not deny this request. Try again.'
        );
      },
    });
  }

  private fail(message: string): void {
    this.errorMessage = message;
    this.state = 'error';
  }
}
